import { DeployStatusCode } from '@core/models/plan.model';
import { escapeHtml } from '@core/utils/html-escape.utils';

export type OverallDeployStatus = 'SUCCESS' | 'ERROR' | 'PROCESSING' | 'NONE';

export interface DeployStatusTooltipEntry {
  name: string;
  status: DeployStatusCode;
  updatedAt?: Date | string | number;
}

/**
 * `expectedChannels` is how many channels a complete deploy covers. A plan whose every
 * attempt succeeded is only *deployed* once it has as many successes as there are channels
 * to deploy — one channel out of five is still in progress, not done. Callers that cannot
 * count the channels omit it and get the old reading: every attempt succeeded is SUCCESS.
 */
export function computeOverallDeployStatus(
  entries: DeployStatusTooltipEntry[],
  expectedChannels?: number
): OverallDeployStatus {
  if (!entries || entries.length === 0) return 'NONE';
  if (entries.some((s) => s.status === 'ERROR')) return 'ERROR';
  if (entries.some((s) => s.status === 'PROCESSING')) return 'PROCESSING';

  const succeeded = entries.filter((s) => s.status === 'SUCCESS').length;
  return expectedChannels && succeeded < expectedChannels ? 'PROCESSING' : 'SUCCESS';
}

export function buildDeployTooltipHtml(
  entries: DeployStatusTooltipEntry[],
  lang: string
): string {
  const formatter = new Intl.DateTimeFormat(lang, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  const items = entries.map((s, i) => {
    const date = s.updatedAt ? formatter.format(new Date(s.updatedAt as string)) : '';
    const colorClass =
      s.status === 'SUCCESS'
        ? 'text-green-400 dark:text-green-600'
        : s.status === 'ERROR'
          ? 'text-red-400 dark:text-red-600'
          : 'text-yellow-400 dark:text-yellow-600';
    const iconClass =
      s.status === 'SUCCESS'
        ? 'ph ph-check-circle'
        : s.status === 'ERROR'
          ? 'ph ph-x-circle'
          : 'ph ph-clock';
    const borderClass = i > 0 ? 'border-t border-zinc-600 dark:border-zinc-300' : '';
    const label = s.status === 'SUCCESS' ? 'OK' : s.status;
    return `<div class="flex flex-col gap-1 py-2 ${borderClass}">
  <div class="flex items-center justify-between gap-4">
    <span class="font-medium">${escapeHtml(s.name)}</span>
    <div class="flex items-center gap-1 ${colorClass}">
      <i class="${iconClass}"></i>
      <span class="text-xs font-semibold">${label}</span>
    </div>
  </div>
  <span class="text-xs opacity-60">${date}</span>
</div>`;
  });
  return `<div class="flex flex-col min-w-56">${items.join('')}</div>`;
}
